import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Box,
  Paper,
  Chip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import { styled } from '@mui/material/styles';
import { userSearchApi } from '../../api/userSearchApi';
import { useHasAccess } from '../../lib/hasAccess';

const MIN_SEARCH_LEN = 2;

/** Maps API `user_status` to compact chip label + MUI Chip color. */
function getUserStatusChipProps(status) {
  if (status == null || status === '') {
    return { label: 'Unknown', color: 'default' };
  }
  const key = String(status).toLowerCase();
  const labelByStatus = {
    active: 'Active',
    inactive: 'Inactive',
    lead: 'Lead',
    'account on hold': 'On hold',
  };
  const label = labelByStatus[key] ?? `${String(status).charAt(0).toUpperCase()}${String(status).slice(1)}`;
  let color = 'default';
  if (key === 'active') {
    color = 'success';
  } else if (key === 'inactive') {
    color = 'error';
  } else if (key === 'lead') {
    color = 'info';
  } else if (key === 'account on hold') {
    color = 'warning';
  }
  return { label, color };
}

function UserStatusTag({ status }) {
  const { label, color } = getUserStatusChipProps(status);
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{
        height: 22,
        maxWidth: 120,
        flexShrink: 0,
        '& .MuiChip-label': {
          px: 0.75,
          fontSize: '0.7rem',
          fontWeight: 600,
          lineHeight: 1.2,
        },
      }}
    />
  );
}

UserStatusTag.propTypes = {
  status: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

const SearchContainer = styled(Box)(() => ({
  position: 'relative',
  width: '300px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.15)'
    : 'rgba(255, 255, 255, 0.1)',
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color', 'box-shadow']),
  '& .MuiOutlinedInput-root': {
    color: theme.palette.mode === 'light' ? '#fff' : theme.palette.text.primary,
    '& input::placeholder': {
      color: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.7)'
        : theme.palette.text.secondary,
      opacity: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.5)'
        : theme.palette.action.hover,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'light'
        ? '#fff'
        : theme.palette.primary.main,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.7)'
        : theme.palette.text.secondary,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.25)'
      : 'rgba(255, 255, 255, 0.15)',
  },
}));

const ResultsList = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1000,
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.background.paper
    : theme.palette.background.default,
  boxShadow: theme.shadows[3],
  '& .MuiListItem-root': {
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? theme.palette.action.hover
        : 'rgba(255, 255, 255, 0.05)',
    },
  },
  '& .MuiListItemText-primary': {
    color: theme.palette.text.primary,
  },
  '& .MuiListItemText-secondary': {
    color: theme.palette.text.secondary,
  },
}));

export default function PurchasesSearchBar() {
  const access = useHasAccess();
  const canSearch = access('globalUserSearch');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const fetchSearchResults = useCallback(
    debounce(async (query) => {
      const q = query.trim();
      if (q.length < MIN_SEARCH_LEN) {
        setSearchResults([]);
        return;
      }
      try {
        const data = await userSearchApi.searchUsers({
          search: q,
          limit: 10,
          offset: 0
        });
        setSearchResults((data.users || []).map((user) => ({
          id: user.id,
          email: user.email,
          fullName: user.name || '',
          user_status: user.user_status,
        })));
      } catch (err) {
        console.error(err);
        setSearchResults([]);
      }
    }, 300),
    []
  );

  const handleQueryChange = (event) => {
    const newQuery = event.target.value;
    setSearchQuery(newQuery);
    fetchSearchResults(newQuery);
  };

  const handleResultClick = (customer) => {
    window.location.href = `/dashboard/customers/${customer.id}`;
    setSearchQuery('');
    setSearchResults([]);
  };

  if (!canSearch) {
    return null;
  }

  return (
    <SearchContainer>
      <StyledTextField
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
        onChange={handleQueryChange}
        placeholder="Search customers by name, email, phone, or DOB..."
        variant="outlined"
        value={searchQuery}
        size="small"
      />
      {searchResults.length > 0 && searchQuery && (
        <ResultsList>
          <List>
            {searchResults.map((result) => (
              <ListItem
                key={result.id}
                button
                onClick={() => handleResultClick(result)}
                sx={{ alignItems: 'flex-start', pr: 1 }}
              >
                <ListItemText
                  primary={(
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        noWrap
                        sx={{ flex: '1 1 auto', minWidth: 0 }}
                      >
                        {result.fullName || 'No name'}
                      </Typography>
                      <UserStatusTag status={result.user_status ?? null} />
                    </Box>
                  )}
                  secondary={result.email || 'No email'}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        </ResultsList>
      )}
    </SearchContainer>
  );
}
